import _ from 'lodash';
import tinycolor from 'tinycolor2';
import { ClusterLayer } from './ClusterLayer';
import { HeatLayer } from './HeatLayer';
import { LegendLayer } from './LegendLayer';
import { TagsLayer } from './TagLayer';
import VectorLayer from './VectorLayer';
import { convertPolygonsToPoints } from './VectorLayerUtils';

// import 'mapbox.js';

export class GeoJSONLayer extends VectorLayer {
    constructor (options) {
        super(options);

        this._addEventHandle = this._addEventHandle.bind(this);
    }

    eachLayer (callback) {
        this.layer.eachLayer(l => callback(l));
    }

    changeIcon (id, icon) {
        if (!id || !icon) {
            return;
        }

        this.state.icons.set(id, icon);

        this.setLayerIcon(this.getLayer(id), icon);
    }

    restoreIcon (id) {
        if (!id) {
            return null;
        }

        try {
            if (this.state.icons.has(id)) {
                this.state.icons.delete(id);

                const layer = this.getLayer(id);
                if (layer) {
                    this.setLayerIcon(layer, this._createStyle(layer.feature).icon);
                }
            }
        } catch (err) {
            console.log('ERROR: ', err);
        }
    }

    setLayerIcon (layer, icon) {
        if (layer && icon) {
            return layer.setIcon(icon);
        }
    }

    _fireMovementEvent (movement) {
        if (this._checkLayerZoomVisibility()) {
            if (!this.getMap().hasLayer(this.layer)) {
                this._showVectors();
            }

            if (this.options.showAll) {
                if (!this._gotAll) {
                    this._gotAll = true;
                    this._makeRequest();
                } else if ((this.options.filter || {}).location) {
                    this._processFeatures(this.toGeoJSON());
                }
            } else if (!this._checkSamePosition(movement) || this.state.features.size === 0) {
                this._makeRequest();
            } else if ((this.options.filter || {}).location) {
                this._processFeatures(this.toGeoJSON());
            }
            return true;
        }
        return false;
    }

    getLayer (id) {
        if (this.layer && _.isFunction(this.layer.getLayers)) {
            return _.find((this.layer.getLayers() || []), l => {
                if (l.feature) {
                    if (_.isArray(this.options.uniqueField)) {
                        return _.some(this.options.uniqueField, f => String(l.feature.properties[f]) === String(id));
                    } else {
                        return String(this._getFeatureId(l.feature)) === String(id);
                    }
                }
                return false;
            });
        }
    }

    _resetLayerStyle () {
        if (this.layer) {
            _(this.layer.getLayers())
                .filter(l => l.feature)
                .forEach(l => {
                    const style = this._createStyle(l.feature);
                    style.force = true;
                    this._changeLayerStyle(l.feature, l, style);
                });
        }

        this.state.highlight = new Map();
    }

    _changeLayerStyle (feature, layer, style) {
        if (!this.state.highlight.get(this._getFeatureId(feature)) || style.force) {
            if (_.isFunction(layer.setStyle)) {
                if (tinycolor(style.color).isValid()) {
                    return layer.setStyle({
                        color: style.color,
                        weight: style.weight
                    });
                } else if (style.icon instanceof L.Icon) {
                    return layer.setStyle({
                        icon: style.icon
                    });
                }
            }
        }
    }

    _createLayer () {
        if (this.layer && this.layer.clearlayers) {
            this.layer.clearlayers();
        }

        // remove current layer if exists
        this._hideVectors();

        return this.checkType('cluster') ? ClusterLayer.create(this.options) : L.featureGroup();
    }

    _highlightLayer (feature, layer, style) {
        layer.on('mouseover', () => {
            this.highlight(feature, { layer, style });
        });
        layer.on('mouseout', () => {
            this.unhighlight(feature, { layer, style });
        });

        if (this.state.highlight.get(this._getFeatureId(feature))) {
            return this.highlight(feature, {
                layer,
                style,
                fixed: true,
                force: true
            });
        }
    }

    _addEventHandle (feature, layer, event, callback) {
        const runCallback = () => callback.call(this, feature, layer);
        if (_.isString(event)) {
            layer.on(event, runCallback);
        } else {
            layer.on(event.trigger, runCallback);
        }
    }

    _onEachFeature (feature, layer) {
        this._addEventHandle(feature, layer, 'click', this._fireClickEvent);
        this._addEventHandle(feature, layer, 'dblclick', this._fireDoubleClickEvent);
        this._addEventHandle(feature, layer, 'mouseover', this._fireMouseoverEvent);
        this._addEventHandle(feature, layer, 'mouseout', this._fireMouseoutEvent);

        if ((this.options.popup || {}).template) {
            this._addEventHandle(feature, layer, { trigger: this.options.popup.trigger || 'click' }, this._showPopup);
            if ((this.options.popup.options || {}).autoclose) {
                this._addEventHandle(feature, layer, { trigger: 'mouseout remove' }, this.closePopup);
            }
        }

        if ((this.options.popup || {}).clusterTemplate) {
            this.layer.on((this.options.popup.trigger ? `cluster${this.options.popup.trigger}` : 'clusterclick'), event => {
                this._showPopup(event.layer.getAllChildMarkers(), event.layer);
            });
            if ((this.options.popup.options || {}).autoclose) {
                this._addEventHandle(feature, this.layer, { trigger: 'clustermouseout' }, this.closePopup);
            }
        }

        if (this.options.label) {
            this._showLabel(feature, layer);
        }

        const style = this._createStyle(feature);
        if (style.highlight) {
            this._highlightLayer(feature, layer, style);
        }

        if (this.state.icons.has(this._getFeatureId(feature))) {
            layer.setIcon(this.state.icons.get(this._getFeatureId(feature)));
        }
        // console.timeEnd "L.VectorLayer.GeoJSON -> _onEachFeature"
    }

    _pointToLayer (feature, latlng) {
        if (this.options.symbology && (this.options.symbology.style || {}).circleMaker) {
            return L.circleMarker(latlng, this._createStyle(feature));
        }

        return L.marker(latlng, this._createStyle(feature));
    }

    // Get the appropriate Leaflet vector options for this feature
    _createStyle (feature) {
        // console.time('L.VectorLayer.GeoJSON -> _createStyle');
        if (!this.options.symbology) {
            return ({});
        }

        const style = this._getStyle(feature);

        const customIcon = feature.properties && feature.properties.Id && this.state.customIcons
            ? this.state.customIcons.filter(x => x.idStore === feature.properties.Id)[0]
            : undefined;

        _.forEach(['icon', 'highlight'], (p) => {
            if (style[p]) {
                if (style[p].iconUrl) {
                    style[p] = customIcon
                        ? customIcon.icon
                        : style[p].className
                          ? L.divIcon({
                              ...style[p],
                              html: `<img src="${style[p].iconUrl}" style="width: ${style[p].iconSize[0]}px; height: ${style[p].iconSize[1]}px;" class="${style[p].className}" />`
                            })
                          : L.icon(style[p]);
                }
            }
        });
        // console.timeEnd('L.VectorLayer.GeoJSON -> _createStyle');
        return style;
    }

    _geojsonToLayer (data) {
        return L.geoJson(data, {
            onEachFeature: (feature, layer) => {
                this._onEachFeature(feature, layer);
            },

            style: feature => {
                return this._createStyle(feature);
            },

            filter: (feature) => {
                return !this.isHidden(this._getFeatureId(feature));
            },

            pointToLayer: (feature, latlng) => {
                return this._pointToLayer(feature, latlng);
            }
        });
    }

    _processFeatures (res) {
        this.setFeatures(res);
        this._startProcessing();
    }

    _startProcessing () {
        const featureCollection = this.getCurrentGeoJSON();
        if (!this.getMap()) { return; }

        this.layer = this._createLayer();
        this.layer.id = this.id;
        this.state.visible = true;
        let _clusterLayer;

        // heat layer doesnt have a removeLayer method, must be recreated in order to work
        if (this.checkType('heat')) {
            this.layer.addLayer(HeatLayer.geoJsonToHeatLayer(featureCollection, { ...this.options, map: this.getMap() }));
        } else if (this.checkType('cluster')) {
            this.layer.addLayer(this._geojsonToLayer(featureCollection));
        } else {
            this._colorRange = (this.options.symbology || {}).type === 'choropleth' ? this._createSymbologyScale(featureCollection) : null;
            this.layer = this._geojsonToLayer(featureCollection);

            if (this.checkType('heat_area')) {
                this.layer.addLayer(HeatLayer.geoJsonToHeatLayer(convertPolygonsToPoints(featureCollection, this.options), { ...this.options, map: this.getMap() }));
            } else if (this.checkType('cluster_area')) {
                _clusterLayer = ClusterLayer.create(this.options);
                _clusterLayer.addLayer(this._geojsonToLayer(convertPolygonsToPoints(featureCollection, this.options)));
                this.layer.addLayer(_clusterLayer);
            }
        }
        // add to map
        this.getMap().addLayer(this.layer); // unless @getMap().hasLayer @layer

        if (this.options.tag) {
            if (!this.checkType('heat')) {
                this._removeTags();
                this._tags = new TagsLayer({
                    features: featureCollection.features,
                    getStyle: this._getStyle,
                    layer: this.layer,
                    map: this.getMap(),
                    options: this.options
                });
            }
        }

        if (this.options.legend) {
            this._removeLegend();
            this._legend = new LegendLayer({
                colorRange: this._colorRange,
                features: featureCollection.features,
                getColorRange: this._getColorRange,
                map: this.getMap(),
                options: this.options
            });
        }

        if (this.options.autofocus) {
            this.getMap().fitBounds(this.layer.getBounds());
        }

        if (this.options.done) {
            this.options.done(featureCollection.features);
        }

        if (this.options.above && _.isFunction(this.layer.bringToFront)) {
            this.layer.bringToFront();
        } else if (this.options.below && _.isFunction(this.layer.bringToBack)) {
            this.layer.bringToBack();
        }

        if (_clusterLayer) {
            _clusterLayer.bringToFront();
        }
    }
}



// WEBPACK FOOTER //
// ./src/scripts/layers/Leaflet/VectorLayer/GeojsonLayer.js